import { ChevronLeftIcon } from '@heroicons/react/16/solid'
import { useCallback, useEffect, useState } from 'react'
import { useParams } from 'react-router-dom'
import { Button } from '../catalyst/button'
import { Heading } from '../catalyst/heading'
import { Link } from '../catalyst/link'
import { Select } from '../catalyst/select'
import { Text } from '../catalyst/text'
import { api } from '../lib/api'
import { useAuth } from '../lib/auth'
import { useField } from '../lib/field'
import { findingLabel, PEST_LOOKUP, pestLookupHref, type ScoutingFinding } from '../lib/scouting'
import type { FieldTask, ScoutingObservation } from '../lib/types'
import { Shell } from '../pages/shell'
import { FIELD_TASKS } from '../wizard/flows/field-task'

// A field's own records that no compliance form asks for: scouting walks and field work. Entries
// are made in the logging wizard; here they are read and, if wrong, deleted.

const formatDate = (iso: string) => new Date(`${iso}T00:00:00`).toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' })

/** One season of a field's entries from `/farms/:farmId/<path>?fieldId=`, newest first. */
function useFieldEntries<T extends { id: string }>(orgId: string | undefined, farmId: string | undefined, fieldId: string | undefined, path: string, year: number) {
  const [entries, setEntries] = useState<T[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const base = orgId && farmId ? `/orgs/${orgId}/farms/${farmId}/${path}` : null

  const load = useCallback(async () => {
    if (!base || !fieldId) return
    try {
      const { data } = await api.getPage<T>(`${base}?${new URLSearchParams({ fieldId, from: `${year}-01-01`, to: `${year}-12-31`, limit: '200' })}`)
      setEntries(data)
      setError(null)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not load the entries.')
    } finally {
      setLoading(false)
    }
  }, [base, fieldId, year])

  useEffect(() => {
    load()
  }, [load])

  const remove = async (id: string) => {
    await api.delete(`${base}/${id}`)
    setEntries((all) => all.filter((e) => e.id !== id))
  }
  return { entries, loading, error, remove }
}

/** Delete asks in place: the button turns into "Yes, delete", and turns back after a few seconds. */
function DeleteButton({ onDelete }: { onDelete: () => Promise<void> }) {
  const [confirming, setConfirming] = useState(false)
  const [busy, setBusy] = useState(false)
  useEffect(() => {
    if (!confirming || busy) return
    const timer = setTimeout(() => setConfirming(false), 5000)
    return () => clearTimeout(timer)
  }, [confirming, busy])
  return confirming ? (
    <Button
      color="red"
      disabled={busy}
      onBlur={() => !busy && setConfirming(false)}
      onClick={async () => {
        setBusy(true)
        try {
          await onDelete()
        } finally {
          setBusy(false)
          setConfirming(false)
        }
      }}
    >
      {busy ? 'Deleting…' : 'Yes, delete'}
    </Button>
  ) : (
    <Button plain onClick={() => setConfirming(true)}>
      Delete
    </Button>
  )
}

function FieldLogPage<T extends { id: string }>({
  path,
  title,
  logKind,
  logLabel,
  dateOf,
  render,
  empty,
}: {
  path: string
  title: string
  logKind: string
  logLabel: string
  dateOf: (e: T) => string
  render: (e: T) => React.ReactNode
  empty: string
}) {
  const { farmId, fieldId } = useParams<{ farmId: string; fieldId: string }>()
  const { org } = useAuth()
  const { field } = useField(org?.id, farmId, fieldId)
  const thisYear = new Date().getFullYear()
  const [year, setYear] = useState(thisYear)
  const { entries, loading, error, remove } = useFieldEntries<T>(org?.id, farmId, fieldId, path, year)

  return (
    <Shell>
      <Link href={`/farms/${farmId}/fields/${fieldId}`} className="inline-flex items-center gap-1 text-sm/6 font-medium text-zinc-500 dark:text-zinc-400">
        <ChevronLeftIcon className="size-4" />
        {field?.name ?? 'Field'}
      </Link>
      <div className="mt-4 flex flex-wrap items-start justify-between gap-4">
        <div>
          <Heading>
            {field?.name} — {title}
          </Heading>
          <Text>Your own record: no compliance form asks for it.</Text>
        </div>
        <div className="flex flex-wrap items-center gap-3">
          <Select value={year} onChange={(e) => setYear(Number(e.target.value))} className="w-28" aria-label="Season">
            {[thisYear, thisYear - 1, thisYear - 2].map((y) => (
              <option key={y} value={y}>
                {y}
              </option>
            ))}
          </Select>
          <Button href={`/farms/${farmId}/log/${logKind}?field=${fieldId}`}>{logLabel}</Button>
        </div>
      </div>
      {error ? (
        <Text className="mt-8 text-red-600 dark:text-red-400">{error}</Text>
      ) : loading ? (
        <Text className="mt-8">Loading…</Text>
      ) : entries.length === 0 ? (
        <div className="mt-8 rounded-lg border border-dashed border-zinc-950/15 p-10 text-center dark:border-white/15">
          <Text>
            {empty} in {year}.
          </Text>
        </div>
      ) : (
        <ul className="mt-8 divide-y divide-zinc-950/5 dark:divide-white/5">
          {entries.map((e) => (
            <li key={e.id} className="flex items-start justify-between gap-4 py-4">
              <div className="min-w-0">
                <p className="text-sm font-semibold text-zinc-950 dark:text-white">{formatDate(dateOf(e))}</p>
                {render(e)}
              </div>
              <DeleteButton onDelete={() => remove(e.id)} />
            </li>
          ))}
        </ul>
      )}
    </Shell>
  )
}

const SEVERITY: Record<string, string> = { low: 'a little', medium: 'some', high: 'a lot' }

export function ScoutingObservationsPage() {
  return (
    <FieldLogPage<ScoutingObservation>
      path="scouting-observations"
      title="Scouting"
      logKind="scouting"
      logLabel="Log scouting"
      empty="No scouting logged"
      dateOf={(o) => o.observedOn}
      render={(o) => (
        <>
          <p className="mt-0.5 text-sm text-zinc-700 dark:text-zinc-300">
            {o.findings.length ? o.findings.map((f) => (f === 'other' ? o.otherFinding || 'something else' : findingLabel(f))).join(', ') : 'Nothing found'}
            {o.severity && <> · {SEVERITY[o.severity]}</>}
          </p>
          {(o.observedBy || o.notes) && <p className="mt-0.5 text-sm text-zinc-500 dark:text-zinc-400">{[o.observedBy, o.notes].filter(Boolean).join(' · ')}</p>}
          {o.findings.filter((f) => pestLookupHref(f as ScoutingFinding)).map((f) => (
            <Link key={f} href={pestLookupHref(f as ScoutingFinding)!} className="mt-1 block text-sm/6">
              Products registered for {PEST_LOOKUP[f as ScoutingFinding]!.short} on blueberry
            </Link>
          ))}
        </>
      )}
    />
  )
}

const hours = (minutes: number) => `${Number((minutes / 60).toFixed(2))} h`

export function FieldTasksPage() {
  return (
    <FieldLogPage<FieldTask>
      path="field-tasks"
      title="Field work"
      logKind="field_task"
      logLabel="Log field work"
      empty="No field work logged"
      dateOf={(t) => t.doneOn}
      render={(t) => (
        <>
          <p className="mt-0.5 text-sm text-zinc-700 dark:text-zinc-300">
            {t.task === 'other' ? t.otherTask || 'Other' : (FIELD_TASKS.find((x) => x.value === t.task)?.label ?? t.task)}
            {t.durationMinutes != null && <> · {hours(t.durationMinutes)}</>}
          </p>
          {(t.performedBy || t.notes) && <p className="mt-0.5 text-sm text-zinc-500 dark:text-zinc-400">{[t.performedBy, t.notes].filter(Boolean).join(' · ')}</p>}
        </>
      )}
    />
  )
}
